<template>
  <div
    class="menu-drawer critical-component-hide"
    :class="[
      `show-tier${currentTier}`,
      { [$class('loading')]: state === 'loading' },
    ]"
  >
    <div
      class="
        menu-drawer__navigation
        critical-component-hide
        critical-component-clear
      "
    >
      <nav class="menu-drawer__menus">
        <ul
          class="menu-drawer__menu menu-drawer__tier1"
          :class="{ [$class('active')]: getActiveState(false) }"
        >
          <li
            v-for="(tier1) of navigation"
            :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
            :key="tier1.handle"
            class="menu-drawer__menu-item"
          >
            <button
              v-if="tier1.links.length"
              class="
                menu-drawer__link
                menu-drawer__button
              "
              :tabindex="getActiveState(false) ? 0 : -1"
              type="button"
              @click="handleLinkClick(tier1)"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.open')"
              />

              {{ tier1.title }}
              <icon-arrow-right />
            </button>

            <a
              v-else
              class="menu-drawer__link"
              :href="tier1.url"
              :tabindex="getActiveState(false) ? 0 : -1"
              v-html="getTier1LinkText(tier1)"
            />

            <ul
              v-if="tier1.links.length"
              class="menu-drawer__menu menu-drawer__tier2"
              :class="{ [$class('active')]: getActiveState(tier1) || getChildActiveState(tier1) }"
            >
              <li class="menu-drawer__menu-item">
                <button
                  :ref="(args) => setItemRefs(args, {
                    handle: tier1.handle,
                    tier: 2,
                    type: 'back',
                  })"
                  class="
                    menu-drawer__link
                    menu-drawer__button
                    menu-drawer__back
                  "
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  type="button"
                  @click="handleLinkClick(false)"
                >
                  <span
                    class="visually-hidden"
                    v-text="backToTopString"
                  />

                  <icon-arrow-left />
                  {{ tier1.title }}
                </button>
              </li>

              <li
                v-for="(tier2) of tier1.links"
                :ref="(args) => setItemRefs(args, {
                  handle: tier1.handle,
                  tier: 2,
                  type: 'link',
                })"
                :key="tier2.handle"
                class="menu-drawer__menu-item"
              >
                <a
                  class="menu-drawer__link"
                  :href="tier2.url"
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  v-text="tier2.title"
                />

                <button
                  v-if="tier2.links.length"
                  class="
                    menu-drawer__link
                    menu-drawer__button
                  "
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  type="button"
                  @click="handleLinkClick(tier2)"
                >
                  <span
                    class="visually-hidden"
                    v-text="$string('accessibility.open')"
                  />
                  <icon-arrow-right />
                </button>

                <ul
                  v-if="tier2.links.length"
                  class="menu-drawer__menu menu-drawer__tier3"
                  :class="{ [$class('active')]: getActiveState(tier2) }"
                >
                  <li class="menu-drawer__menu-item">
                    <button
                      :ref="(args) => setItemRefs(args, {
                        handle: tier2.handle,
                        tier: 3,
                        type: 'back',
                      })"
                      class="
                        menu-drawer__link
                        menu-drawer__button
                        menu-drawer__back
                      "
                      :tabindex="getActiveState(tier2) ? 0 : -1"
                      type="button"
                      @click="handleLinkClick(tier1)"
                    >
                      <icon-arrow-left />

                      <span
                        class="visually-hidden"
                        v-text="$string('general.menu_drawer.back_to')"
                      />

                      {{ tier2.title }}
                    </button>
                  </li>

                  <li
                    v-for="(tier3) of tier2.links"
                    :ref="(args) => setItemRefs(args, {
                      handle: tier2.handle,
                      tier: 3,
                      type: 'link',
                    })"
                    :key="tier3.handle"
                    class="menu-drawer__menu-item"
                  >
                    <a
                      class="menu-drawer__link"
                      :href="tier3.url"
                      :tabindex="getActiveState(tier2) ? 0 : -1"
                      v-text="tier3.title"
                    />
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </div>

    <div
      class="
        menu-drawer__signposts
        critical-component-hide
        critical-component-clear
      "
    >
      <div
        v-for="signpost, index of signposts"
        :key="index"
        class="menu-drawer__signpost"
        :class="{ 'active': currentLink.title === signpost.key }"
      >
        <a
          v-if="checkSignpostLink(signpost)"
          class="
            menu-drawer__signpost-link
            mega-nav__signpost-link
          "
          :href="signpost.link"
        >
          <div
            class="menu-drawer__image-wrap"
          >
            <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
            <img :src="signpost.image">
          </div>

          <span v-text="signpost.cta_copy" />
        </a>

        <div
          v-else
          class="menu-drawer__image-wrap"
        >
          <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
          <img :src="signpost.image">
        </div>
      </div>
    </div>

    <div
      v-if="currentTier === 1"
      class="menu-drawer__footer"
    >
      <div
        v-if="$variable('customer.enabled')"
        class="menu-drawer__login-account"
      >
        <icon-account />

        <a
          v-if="$variable('customer.loggedIn')"
          :href="$variable('routes.account.url')"
          :tabindex="getActiveState(false) ? 0 : -1"
          v-text="$string('general.menu_drawer.account')"
        />

        <div v-else>
          <a
            :href="$variable('routes.account.login')"
            :tabindex="getActiveState(false) ? 0 : -1"
            v-text="$string('general.menu_drawer.login')"
          />

          /

          <a
            :href="$variable('routes.account.register')"
            :tabindex="getActiveState(false) ? 0 : -1"
            v-text="$string('general.menu_drawer.register')"
          />
        </div>
      </div>

      <a
        v-if="shippingLink"
        class="menu-drawer__shipping-link"
        :href="shippingLink"
        v-text="$string('general.menu_drawer.shipping_link')"
      />

      <div class="menu-drawer__currency-switcher">
        <slot name="currency" />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Menu drawer
 * -----------------------------------------------------------------------------
 * Mobile menu drawer.
 * - CSS variable --menu-drawer-links-height is updated to match height of
 *   current visible links.
 *
 * @param {Boolean} isActive - If the containing overlay is active.
 * @param {Object} navigation - Object with links to display.
 * @param {String} shippingLink - URL for shipping & returns link.
 * @param {Object} signposts - Featured images and links for panels.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconArrowLeft from '~icons/direction/arrow/left.svg'
import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconAccount from '~icons/misc/account.svg'

export default {
  name: 'MenuDrawer',

  components: {
    IconAccount,
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Array,
      default: () => [],
    },
    shippingLink: {
      type: String,
      default: '',
    },
    signposts: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      currentLink: false,
      currentHeight: false,
      itemRefs: [],
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute currency code for dropdown.
     * @returns {String}
     */
    formatCurrency() {
      const { currency, currencySymbol } = window.cnvs.store

      return ` ${currency}(${currencySymbol})`
    },

    /**
     * Compute back to top string (max-length issue).
     * @returns {String}
     */
    backToTopString() {
      return `${this.$string('general.menu_drawer.back_to')} ${this.$string('general.menu_drawer.top_level')}`
    },

    /**
     * Compute current item refs in current link.
     * @returns {Array}
     */
    currentItemRefs() {
      if (this.currentLink) {
        return this.itemRefs.filter((itemRef) => {
          return itemRef.handle === this.currentLink.handle
        })
      }

      return this.itemRefs.filter((itemRef) => itemRef.tier === 1)
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },
  },

  watch: {

    /**
     * Watch current link to update focus.
     */
    currentLink() {
      this.updateFocus()
      this.updateHeight()
    },

    /**
     * Watch active overlay to reset current link.
     */
    getActiveOverlay() {
      this.currentLink = false
    },

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('menuDrawer')
    },
  },

  mounted() {
    this.removeStaticMenu()
    this.updateFocus()
    this.updateHeight()
    this.state = 'ready'
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Check signposts have CTA text and link.
     * @param {Object} signpost - Signpost object.
     * @returns {Boolean}
     */
    checkSignpostLink(signpost) {
      const { cta_copy, link } = signpost

      // eslint-disable-next-line camelcase
      if (cta_copy !== '' && link !== null) {
        return true
      }

      return false
    },

    /**
     * Get tier 1 link text or loading placeholder based on state.
     * @param {Object} tier - Tier 1 object.
     * @returns {String}
     */
    getTier1LinkText(tier) {
      if (this.state === 'loading') {
        return `<span class="menu-drawer__loading"></span>`
      }

      return tier.title
    },

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return this.isActive && link.handle === this.currentLink.handle
    },

    /**
     * Get active state of current nested menu's child menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getChildActiveState(link) {
      const tier3LinkActive = link.links.find((tier3Link) => {
        return tier3Link.handle === this.currentLink.handle
      })

      return Boolean(tier3LinkActive)
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Item in v-for.
     * @param {String} data.handle - Handle of item.
     * @param {Number} data.tier - Tier of item.
     * @param {String} data.type - Type of item, `link` or `back`.
     */
    setItemRefs(element, data) {
      if (!element) {
        return
      }

      this.itemRefs.push({
        ...data,
        element,
      })
    },

    /**
     * Removes static menu drawer rendered by menu-drawer.snippet.liquid.
     * - Otherwise there would be duplicate menus.
     */
    removeStaticMenu() {
      const container = document.querySelector('[js-menu-drawer="container"]')

      if (!container) {
        return
      }

      container.remove()
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick().then(() => {
        cnvs.EventBus.emit('overlay:update', 'menuDrawer')
      })
    },

    /**
     * Update height of current active navigation.
     * - Goes through each item in currentItemRefs and get height of element.
     */
    updateHeight() {
      nextTick().then(() => {
        let height = 0

        this.currentItemRefs.forEach((ref) => {
          if (!ref?.element?.offsetHeight) {
            return
          }

          height += ref.element.offsetHeight
        })

        /**
         * If height is 0 then re-run function as it's a bug.
         */
        if (height === 0 && this.currentItemRefs.length) {
          setTimeout(() => {
            this.updateHeight()
          }, 100)

          return
        }

        /**
         * Set CSS variable.
         */
        document.documentElement.style.setProperty(
          '--menu-drawer-links-height',
          `${height}px`,
        )

        /**
         * Update Vue data for easier debugging.
         */
        this.currentHeight = height
      })
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkClick(link) {
      this.currentLink = link
    },
  },
}
</script>

<style lang="scss">
@import './menu-drawer';
</style>
